/**
 * Generalized offline queue.
 *
 * Stores pending writes (any method, path, body) in localStorage under
 * `stockaris_offline_queue`. When the network returns, `drain({baseUrl})`
 * replays each entry in order and removes the ones that succeed.
 *
 * Public API:
 *   enqueue({ path, method, body, label })        — push a pending write
 *   enqueuePick({ pickListId, payload })          — backwards-compat helper
 *   peek()                                        — read the current queue
 *   count()                                       — number of pending entries
 *   clearAll()                                    — wipe the queue
 *   subscribe(cb)                                 — react to queue changes
 *   getLastSyncedAt() / setLastSyncedAt()         — timestamp of last drain
 *   drain({ baseUrl })                            — retry everything
 *
 * Legacy `pickListId` entries from iter 85 are auto-migrated on first read
 * so no picks are lost when the client upgrades.
 */
const KEY = 'stockaris_offline_queue';
const LEGACY_KEY = 'stockaris_offline_picks';
const LAST_SYNCED_KEY = 'stockaris_offline_last_synced_at';
const listeners = new Set();

function migrateLegacy() {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const list = JSON.parse(legacy);
    if (!Array.isArray(list) || list.length === 0) {
      localStorage.removeItem(LEGACY_KEY);
      return;
    }
    const migrated = list
      .filter((e) => e && e.pickListId)
      .map((e) => ({
        path: `/api/pick-lists/${e.pickListId}/pick-item`,
        method: 'POST',
        body: e.payload || {},
        label: `Pick · ${e.pickListId.slice(0, 8)}`,
        enqueued_at: e.enqueued_at || Date.now(),
      }));
    const existing = rawRead();
    localStorage.setItem(KEY, JSON.stringify([...migrated, ...existing]));
    localStorage.removeItem(LEGACY_KEY);
  } catch (_) {
    /* ignore */
  }
}

function rawRead() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function read() {
  migrateLegacy();
  return rawRead();
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch (_) {
    /* quota exceeded */
  }
  listeners.forEach((cb) => {
    try { cb(list); } catch (_) { /* ignore subscriber errors */ }
  });
}

export function enqueue(entry) {
  if (!entry || !entry.path) return 0;
  const list = read();
  list.push({
    path: entry.path,
    method: (entry.method || 'POST').toUpperCase(),
    body: entry.body || {},
    label: entry.label || entry.path,
    enqueued_at: Date.now(),
  });
  write(list);
  return list.length;
}

// Backwards-compat helper kept so iter 85's Picking flow still works
export function enqueuePick({ pickListId, payload }) {
  return enqueue({
    path: `/api/pick-lists/${pickListId}/pick-item`,
    method: 'POST',
    body: payload,
    label: `Pick · ${String(pickListId).slice(0, 8)}`,
  });
}

export function peek() {
  return read();
}

export function count() {
  return read().length;
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function clearAll() {
  write([]);
}

export function getLastSyncedAt() {
  const v = Number(localStorage.getItem(LAST_SYNCED_KEY) || 0);
  return v > 0 ? v : null;
}

export function setLastSyncedAt(ts = Date.now()) {
  try { localStorage.setItem(LAST_SYNCED_KEY, String(ts)); } catch (_) { /* ignore */ }
}

/**
 * Replay queued entries sequentially. Drops 2xx + 4xx, keeps 5xx + network
 * failures. Updates `last_synced_at` if at least one entry was drained.
 * Returns { drained, failed, remaining }.
 */
export async function drain({ baseUrl }) {
  const queue = read();
  if (queue.length === 0) return { drained: 0, failed: 0, remaining: 0 };

  let drained = 0;
  let failed = 0;
  const survivors = [];

  for (const entry of queue) {
    try {
      const url = entry.path.startsWith('http') ? entry.path : `${baseUrl}${entry.path}`;
      const init = {
        method: entry.method || 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      };
      if (init.method !== 'GET' && init.method !== 'HEAD') {
        init.body = JSON.stringify(entry.body || {});
      }
      const resp = await fetch(url, init);
      if (resp.ok) {
        drained += 1;
      } else if (resp.status >= 500) {
        survivors.push(entry);
      } else {
        failed += 1;
      }
    } catch (_) {
      survivors.push(entry);
    }
  }
  write(survivors);
  if (drained > 0 || failed > 0) setLastSyncedAt(Date.now());
  return { drained, failed, remaining: survivors.length };
}
